<template>
    <div class="flex flex-col sm:flex-row items-center justify-between w-full">
        <div class="flex w-full items-center justify-center sm:justify-start text-center select-none text-lg mb-4 sm:mb-0" :class="{ 'invisible': 1 === this.page && 1 === this.pages }">
            <a class="hidden lg:block text-sm px-3 py-2 text-gray-900 hover:text-white bg-gray-100 hover:bg-orange-300 hover:font-medium rounded mx-1" v-if="2 < page" href="#" @click.prevent="setPage(1)">
                <span class="fal fa-angle-double-left fa-fw"></span>
            </a>

            <a class="block text-sm px-3 py-2 text-gray-900 hover:text-white bg-gray-100 hover:bg-orange-300 hover:font-medium rounded mx-1" v-if="1 < page" href="#" @click.prevent="setPage(page - 1)">
                <span class="fal fa-angle-left fa-fw"></span>
            </a>

            <a class="md:hidden text-sm px-3 py-2 text-gray-900 hover:text-white bg-gray-100 hover:bg-orange-300 hover:font-medium rounded mx-1" v-for="offset in [2, 1]" v-if="page - offset >= 1" href="#" @click.prevent="setPage(page - offset)" v-text="page - offset"></a>
            <a class="hidden md:block text-sm px-3 py-2 text-gray-900 hover:text-white bg-gray-100 hover:bg-orange-300 hover:font-medium rounded mx-1" v-for="offset in [3, 2, 1]" v-if="page - offset >= 1" href="#" @click.prevent="setPage(page - offset)" v-text="page - offset"></a>

            <span class="text-sm px-3 py-2 text-white bg-orange-400 font-medium rounded mx-1" v-text="page"></span>

            <a class="md:hidden text-sm px-3 py-2 text-gray-900 hover:text-white bg-gray-100 hover:bg-orange-300 hover:font-medium rounded mx-1" v-for="offset in [1, 2]" v-if="page + offset <= pages" href="#" @click.prevent="setPage(page + offset)" v-text="page + offset"></a>
            <a class="hidden md:block text-sm px-3 py-2 text-gray-900 hover:text-white bg-gray-100 hover:bg-orange-300 hover:font-medium rounded mx-1" v-for="offset in [1, 2, 3]" v-if="page + offset <= pages" href="#" @click.prevent="setPage(page + offset)" v-text="page + offset"></a>

            <a class="block text-sm px-3 py-2 text-gray-900 hover:text-white bg-gray-100 hover:bg-orange-300 hover:font-medium rounded mx-1" v-if="page < pages" href="#" @click.prevent="setPage(page + 1)">
                <span class="fal fa-angle-right fa-fw"></span>
            </a>

            <a class="hidden lg:block text-sm px-3 py-2 text-gray-900 hover:text-white bg-gray-100 hover:bg-orange-300 hover:font-medium rounded mx-1" v-if="page < pages - 2" href="#" @click.prevent="setPage(pages)">
                <span class="fal fa-angle-double-right fa-fw"></span>
            </a>
        </div>

        <div class="flex items-center ml-auto">
            <label class="hidden lg:block input-label whitespace-nowrap mr-2" for="perPage">Show</label>

            <div class="relative w-18 mr-2">
                <div class="has-icon">
                    <!--
                        These options assume 7 database brick tiles, and then 1 promotional tile.
                        So 14 per page will actually display 16 tiles, including the promo tiles.
                    -->
                    <select class="input !w-16 appearance-none" v-model="perPage" id="perPage">
                        <option v-for="option in [14, 28, 42, 56]" :value="option" v-text="option"></option>
                    </select>
                </div>

                <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                    <i class="fal fa-angle-down" aria-hidden="true"></i>
                </div>
            </div>

            <label class="input-label whitespace-nowrap" for="perPage">Per Page</label>
        </div>
    </div>
</template>

<script>
    import { assign, isEqual } from 'lodash-es';

    export default {
        props: {
            pagination: {
                type: Object,
                required: false,
            },

            value: {
                type: Object,
                required: false,
            },
        },

        data() {
            return {
                perPage: this.value ? Number(this.value.perPage) : null,
                page: this.value ? Number(this.value.page) : null,
                firstLoad: true,
                focusedIndex: null,
            };
        },

        watch: {
            value() {
                this.perPage = Number(this.value.perPage);
                this.page = Number(this.value.page);

                if (null !== this.focusedIndex) {
                    const index = this.focusedIndex % this.perPage;

                    this.$emit('focus', index);

                    this.focusedIndex = null;
                }

                if (this.firstLoad || (! this.firstLoad && this.focusedIndex)) {
                    return;
                }

                // Get the brick result element based on its sku
                const element = this.$parent.$el;

                if (element === null) {
                    return;
                }

                // Scroll up to the start of the results.
                // Must account for the size of the fixed header nav bar.
                const scrollAnchor = document.querySelector('#scroll-anchor');
                const headerHeight = document.querySelector('#top-nav-bar').offsetHeight;
                const elementTop = scrollAnchor.getBoundingClientRect().top + window.scrollY;
                window.scrollTo({
                    top: elementTop - headerHeight - 8,
                    behavior: 'smooth'
                });
            },

            perPage(value, previous) {
                if (! this.firstLoad) {
                    let position = 1;

                    if (this.page !== 1) {
                        // We need to make sure we're looking at the current page
                        // and not the next page by doing `(this.page - 1)`
                        position = previous * (this.page - 1) + 1;

                        // Our page is now starting from 0, we need it
                        // to be starting from 1 instead so we `+ 1`
                        const page = Math.floor(position / value) + 1;

                        // The Math.max is mainly for safety here, though it
                        // should never really be triggered of a < 1
                        this.page = Math.max(page, 1);
                    }

                    // Position = start at 1, Index = start at 0
                    this.focusedIndex = position - 1;
                }

                this.mutateQuery();

                this.firstLoad = false;
            },

            page(value, previous) {
                this.mutateQuery();
            },
        },

        computed: {
            pages() {
                if (null === this.pagination || null === this.pagination.meta) {
                    return 1;
                }

                return this.pagination.meta.last_page;
            },
        },

        methods: {
            mutateQuery() {
                let query = assign({}, this.$route.query, {
                    page: Number(this.page),
                    per_page: Number(this.perPage),
                });

                if (1 === query.page) {
                    delete query.page;
                }

                if (28 === query.per_page) {
                    delete query.per_page;
                }

                // only push the route if the query has changed
                if (!isEqual(query, this.$route.query)) {
                    this.$router.push(assign({}, this.$route, { query }));
                }
            },

            setPage(page) {
                this.page = page;
            },
        },
    };
</script>
